import { AutocompleteOption } from 'redesign';
import {
  ShiftPlanningFiltersBranchOffice,
  ShiftPlanningFiltersJobType,
  ShiftPlanningFiltersPlanner,
} from 'hq/services/shift-planning-service.types';
import { DepartmentOptions, ShiftPlanningFiltersData } from '../shift-planning-filters.types';
import { ShiftPlanningFilters } from '../../table/hooks/use-shift-planning.types';

export const checkBranchOfficesInCompany = (
  branchOfficeNames: string[],
  shiftPlanningFiltersData: ShiftPlanningFiltersData
): boolean => {
  const allBranchOfficeNames =
    shiftPlanningFiltersData.companies
      ?.flatMap((company) => company.branchOffices)
      .map((branchOffice) => branchOffice.branchOfficeName) || [];

  // Check if all branch office names are present
  return branchOfficeNames.every((name) => allBranchOfficeNames.includes(name));
};

export const getPartner = (
  appliedFilters: ShiftPlanningFilters,
  partners: ShiftPlanningFiltersPlanner[]
) => {
  if (!appliedFilters) {
    return undefined;
  }
  const partner = partners.find((databasePartner) => {
    return databasePartner.id === appliedFilters.partner;
  });
  if (partner) {
    return { value: partner.id, label: partner.name };
  }
  return undefined;
};

export const getJobTypes = (
  appliedJobTypes: string[] | undefined,
  jobTypes: ShiftPlanningFiltersJobType[]
): AutocompleteOption[] => {
  const cleanedJobTypes: AutocompleteOption[] = [];
  if (!appliedJobTypes || !appliedJobTypes.length) {
    return [];
  }
  appliedJobTypes.forEach((jobTypeParamId) => {
    const jobType = jobTypes.find((databaseJobType) => {
      return databaseJobType.id === jobTypeParamId;
    });
    if (!jobType) {
      return;
    }

    cleanedJobTypes.push({ value: jobType.id, label: jobType.name } as AutocompleteOption);
  });

  return cleanedJobTypes;
};

export const getBranchOffices = (
  appliedBranchOfficeIds: string[] | undefined,
  branchOffices: ShiftPlanningFiltersBranchOffice[]
): AutocompleteOption[] => {
  const options: AutocompleteOption[] = [];

  if (!appliedBranchOfficeIds?.length) {
    return options;
  }

  appliedBranchOfficeIds.forEach((branchOfficeParamId) => {
    const branchOffice = branchOffices.find((b) => b.branchOfficeId === branchOfficeParamId);

    if (branchOffice) {
      options.push({
        value: branchOffice.branchOfficeId,
        label: branchOffice.branchOfficeName,
      });
    }
  });

  return options;
};

export const getDepartments = (
  appliedDepartmentIds: string[] | undefined,
  branchOffices: ShiftPlanningFiltersBranchOffice[]
): DepartmentOptions[] => {
  if (!appliedDepartmentIds?.length) {
    return [];
  }
  const allDepartments: DepartmentOptions[] = branchOffices.flatMap((b) =>
    b.departments.map((d) => ({
      value: d.departmentId,
      label: d.departmentName,
      branchOfficeName: b.branchOfficeName,
    }))
  );

  return appliedDepartmentIds
    .map((deptId) => allDepartments.find((d) => d.value === deptId))
    .filter((d): d is DepartmentOptions => !!d);
};
